import { DateTime } from 'luxon';

import type { DayTradingHours } from './config';
import config from './config';

export const NEW_YORK_TIMEZONE = 'America/New_York';

export type LeaderboardTimePeriod = 'daily' | 'weekly' | 'monthly' | 'yearly' | 'all_time';

const currentUnixTime = (): number => Math.round(new Date().getTime() / 1000);

const shortDurationFormatter = (sec_num: number): string => {
    const days = Math.floor(sec_num / (3600 * 24));
    const hours = Math.floor((sec_num % (3600 * 24)) / 3600);
    const minutes = Math.floor((sec_num % 3600) / 60);
    const seconds = Math.floor(sec_num % 60);

    let hourForm = hours.toString();
    const minForm = minutes.toString();
    let secForm = seconds.toString();

    if (seconds < 10) {
        secForm = '0' + seconds;
    }

    if (days > 0) {
        if (hours < 10) {
            hourForm = '0' + hours;
        }

        return days + 'd ' + hourForm + 'h ' + minForm + 'm ' + secForm + 's';
    }

    if (hours > 0) {
        return hourForm + 'h ' + minForm + 'm ' + secForm + 's';
    }

    return minForm + 'm ' + secForm + 's';
};

export const shortDurationLetterFormatter = (sec_num: number): string => {
    const days = Math.floor(sec_num / (3600 * 24));
    const hours = Math.floor((sec_num % (3600 * 24)) / 3600);
    const minutes = Math.floor((sec_num % 3600) / 60);
    const seconds = Math.floor(sec_num % 60);

    const hourForm = hours.toString();
    const minForm = minutes.toString();
    const secForm = seconds.toString();

    if (days > 0) {
        return days + 'd ' + hourForm + 'h ' + minForm + 'm ' + secForm + 's';
    }

    if (hours > 0) {
        return hourForm + 'h ' + minForm + 'm ' + secForm + 's';
    }

    return minForm + 'm ' + secForm + 's';
};

const durationFormatter = (seconds: number): string => {
    let interval = seconds / 31536000;

    if (interval > 1) {
        const years = Math.floor(interval);
        if (years > 1) {
            return years + ' years';
        }

        return '1 year';
    }

    interval = seconds / 86400;
    if (interval > 1) {
        const days = Math.floor(interval);
        if (days > 1) {
            return days + ' days';
        }

        return '1 day';
    }

    return shortDurationFormatter(seconds);
};

const timeTo = (date: number): string => {
    const seconds = Math.floor(date - DateTime.now().toSeconds());

    return durationFormatter(seconds);
};

const timeSince = (date: number): string => {
    const seconds = Math.floor(DateTime.now().toSeconds() - date);

    return durationFormatter(seconds);
};

const secondsTo = (date: string): number => {
    return Math.floor(DateTime.fromISO(date).diffNow().as('seconds'));
};

function twitterTime(date: number, now: number = Date.now()): string {
    const seconds = Math.floor((now - date) / 1000);

    const intervalHours = seconds / 3600; // Convert seconds to hours
    if (intervalHours > 24) {
        const datetime = DateTime.fromMillis(date);
        if (datetime.year === DateTime.now().year) {
            return DateTime.fromMillis(date).toFormat('MMM d');
        } else {
            return DateTime.fromMillis(date).toFormat('MMM d, yyyy');
        }
    }
    if (intervalHours > 1) {
        return Math.floor(intervalHours) + 'h';
    }
    const intervalMinutes = seconds / 60;
    if (intervalMinutes > 1) {
        return Math.floor(intervalMinutes) + 'm';
    }
    return 'Now';
}

function notificationTime(date: number): string {
    const seconds = Math.floor((new Date().getTime() - date) / 1000);
    const intervalMinutes = seconds / 60;
    const intervalHours = seconds / 3600;

    // Below 1 minute: "Now"
    if (intervalMinutes < 1) {
        return 'Now';
    }

    // Below 1 hour: show minutes (e.g., "59m")
    if (intervalHours < 1) {
        return Math.floor(intervalMinutes) + 'm';
    }

    // Below 1 day: show time (e.g., "10:00am")
    if (intervalHours < 24) {
        return DateTime.fromMillis(date).toFormat('h:mma').toLowerCase();
    }

    // Everything else: show date (e.g., "Jan 1")
    return DateTime.fromMillis(date).toFormat('MMM d');
}

const underMonth = (date: number): boolean => {
    const asDateTime = DateTime.fromMillis(date);
    const now = DateTime.now().toUTC();
    const timeElapsed = now.diff(asDateTime.toUTC());
    if (timeElapsed.as('months') >= 1) {
        return false;
    }

    return true;
};

const toTimeStringWithTz = (dateTime: DateTime, capitalCaseMeridiam = false): string => {
    const answerString = dateTime.toFormat('h:mma ZZZZ');
    if (capitalCaseMeridiam) {
        return answerString;
    }
    return answerString.replace('AM', 'am').replace('PM', 'pm');
};

const toDateString = (dateTime: DateTime): string => {
    return dateTime.toFormat('MMM d, yyyy');
};

const toDateTimeStringWithTz = (dateTime: DateTime, capitalCaseMeridiam = false): string => {
    return `${toDateString(dateTime)} at ${toTimeStringWithTz(dateTime, capitalCaseMeridiam)}`;
};

const toShortDateString = (dateTime: DateTime): string => {
    return dateTime.toFormat('MMM d');
};

const toShortDateTimeStringWithTz = (dateTime: DateTime, capitalCaseMeridiam = false): string => {
    return `${toShortDateString(dateTime)} at ${toTimeStringWithTz(dateTime, capitalCaseMeridiam)}`;
};

const isoToDateTimeStringWithTz = (isoString: string, capitalCaseMeridiam = false): string => {
    return toDateTimeStringWithTz(DateTime.fromISO(isoString), capitalCaseMeridiam);
};

type WeekDayNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7;
type WeekDayString = 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday';
const getWeekDayStringFromNumber = (weekday: WeekDayNumber): WeekDayString => {
    switch (weekday) {
        case 1:
            return 'Monday';
        case 2:
            return 'Tuesday';
        case 3:
            return 'Wednesday';
        case 4:
            return 'Thursday';
        case 5:
            return 'Friday';
        case 6:
            return 'Saturday';
        case 7:
            return 'Sunday';
    }
};

interface ExchangeHoursParsed {
    start: DateTime;
    end: DateTime;
}

const getTodaysTradingHours = (dt: DateTime): ExchangeHoursParsed[] => {
    const weekday = dt.setZone(NEW_YORK_TIMEZONE).weekday as WeekDayNumber;
    // `tradingHoursConfig` is parsed from an env-provided JSON string and may be missing
    // (or only partially override) a weekday key. Treat a missing entry as no trading-hour
    // windows for that day so `.map()` below never throws on `undefined`.
    const exchangeHours: DayTradingHours[] = config.tradingHoursConfig[getWeekDayStringFromNumber(weekday)] ?? [];
    const exchangeHoursParsed: ExchangeHoursParsed[] = exchangeHours.map((hour) => {
        const [startHour, startMinute] = hour.open_time.split(':').map(Number);
        const [endHour, endMinute] = hour.close_time.split(':').map(Number);
        const startTime = dt.setZone(NEW_YORK_TIMEZONE).set({ hour: startHour, minute: startMinute, second: 0 });
        const closesNextDay = endHour < startHour || endHour === 0; // If end hour is less than start hour, it means the trading hours extend to the next day.
        const endTime = closesNextDay
            ? dt.setZone(NEW_YORK_TIMEZONE).set({ hour: endHour, minute: endMinute, second: 0, weekday: weekday + 1 })
            : dt.setZone(NEW_YORK_TIMEZONE).set({ hour: endHour, minute: endMinute, second: 0 });
        return { start: startTime, end: endTime };
    });

    return exchangeHoursParsed;
};

// Returns undefined when the day has zero configured trading-hour windows. Callers must
// handle the no-trading-hours case rather than assuming a window always exists.
const getTradingHours = (dateTime?: DateTime): ExchangeHoursParsed | undefined => {
    const dt = dateTime || DateTime.now().setZone(NEW_YORK_TIMEZONE);
    const todaysTradingHours = getTodaysTradingHours(dt);
    const selectedExchangeHoursParsed: ExchangeHoursParsed | undefined =
        todaysTradingHours.filter((hour) => {
            return dt >= hour.start && dt <= hour.end;
        })[0] || todaysTradingHours[0];

    return selectedExchangeHoursParsed;
};

// To be called *only* outside of trading hours. Meaningless during trading hours.
const getOutsideTradingHoursMessage = (): string => {
    // Today's trading hours
    const now = DateTime.now().setZone(NEW_YORK_TIMEZONE);
    const tradingHours = getTradingHours(now);

    // Next window starting later today, if any.
    const todaysTradingHours = getTodaysTradingHours(now);
    const nextTodaysTradingHours = todaysTradingHours.filter((hour) => {
        return now < hour.start;
    });

    // Determine when trading resumes. Prefer a window still upcoming today; otherwise advance
    // forward day-by-day until we find the next day that has at least one configured window,
    // and use that day's first window's start. Missing weekday keys are a supported
    // "no windows" state, so we can't stop at tomorrow. Cap the search at 7 days so a fully
    // empty config can never loop forever (falls through to the '' guard below).
    let nextTradingHoursStart: DateTime | undefined;
    if (nextTodaysTradingHours.length > 0) {
        nextTradingHoursStart = nextTodaysTradingHours[0].start;
    } else {
        for (let dayOffset = 1; dayOffset <= 7; dayOffset++) {
            const futureWindows = getTodaysTradingHours(now.plus({ days: dayOffset }));
            if (futureWindows.length > 0) {
                nextTradingHoursStart = futureWindows[0].start;
                break;
            }
        }
    }

    // Either value may be undefined when a day has no configured trading hours; bail out
    // with an empty message rather than dereferencing `.start` on undefined.
    const tradingWillResumeDt = tradingHours && now < tradingHours.start ? tradingHours.start : nextTradingHoursStart;
    if (!tradingWillResumeDt) {
        return '';
    }

    const tradingWillResumeString = tradingWillResumeDt.toFormat('h:mm a ZZZZ');

    return `Resumes at ${tradingWillResumeString}`;
};

const getDaysDifference = (date1: Date, date2: Date): number => {
    const difference = date2.getTime() - date1.getTime();
    return difference / (1000 * 60 * 60 * 24.0);
};

const daysUntil = (date: string): number => {
    const today = new Date();
    const target = new Date(date);
    return getDaysDifference(today, target);
};

const daysSince = (date: string): number => {
    const today = new Date();
    const target = new Date(date);
    return getDaysDifference(target, today);
};

const daysUntilRoundUp = (date: string): number => {
    return Math.ceil(daysUntil(date));
};

const getIsClosingSoon = (closeDate: string | undefined, isClosed: boolean, eventTicker: string) => {
    const hideClosingStatsTickers = [
        'KXSB-25',
        'KXSBADS-25',
        'KXSBMENTION-25',
        'KXSBPERFORM-25',
        'KXSBSETLISTS-25',
        'KXRAINNOSB-25FEB09',
    ];
    if (!closeDate || hideClosingStatsTickers.includes(eventTicker)) {
        return false;
    }
    return !isClosed && daysUntil(closeDate) < 1;
};

// This fucntion needs to be pure and not rely on any external state, so we pass in the current time as a parameter for pre-rendering purposes.
export const isPast = (date: string, now: number = Date.now()) => {
    if (!date) return false;
    const target = new Date(date);
    return target.getTime() - now < 0;
};

const getIsClosed = (closeDate?: string, now: number = Date.now()) => {
    if (!closeDate) return false;
    return isPast(closeDate, now);
};

const getIsOpen = (openDate: string, now: number = Date.now()) => {
    return isPast(openDate, now);
};

function getLocalTimezoneAbbreviation() {
    const now = new Date();
    const dtf = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' });
    const [{ value: tz }] = dtf.formatToParts(now).filter((part) => part.type === 'timeZoneName');
    return tz;
}

function secondsLeftInDay(): number {
    const now = DateTime.now().setZone(NEW_YORK_TIMEZONE);
    const tomorrowStart = now.plus({ days: 1 }).startOf('day');
    return Math.max(0, Math.floor(tomorrowStart.diff(now).as('seconds')));
}

function secondsLeftInWeek(): number {
    // Countdown to next Monday 00:00 America/New_York time
    const now = DateTime.now().setZone(NEW_YORK_TIMEZONE);
    let daysToAdd = (8 - now.weekday) % 7;
    if (daysToAdd === 0) {
        daysToAdd = 7;
    }
    const nextMondayStart = now.plus({ days: daysToAdd }).startOf('day');
    return Math.max(0, Math.floor(nextMondayStart.diff(now).as('seconds')));
}

function secondsLeftInMonth(): number {
    const now = DateTime.now().setZone(NEW_YORK_TIMEZONE);
    const nextMonthStart = now.plus({ months: 1 }).startOf('month');
    return Math.max(0, Math.floor(nextMonthStart.diff(now).as('seconds')));
}

function secondsLeftInYear(): number {
    // Countdown to Jan 1 00:00 of the next year (America/New_York time)
    const now = DateTime.now().setZone(NEW_YORK_TIMEZONE);
    const nextYearStart = now.plus({ years: 1 }).startOf('year');
    return Math.max(0, Math.floor(nextYearStart.diff(now).as('seconds')));
}

function formattedTimeToMidnightUTC(): string {
    const now = DateTime.now().setZone('UTC');
    const tomorrow = now.plus({ days: 1 }).startOf('day');
    const duration = tomorrow.diff(now);
    return duration.toFormat("h'h' m'm' s's'");
}

const isTodayOrYesterday = (date: string): boolean => {
    return (
        DateTime.fromISO(date).setZone(NEW_YORK_TIMEZONE).hasSame(DateTime.now().setZone(NEW_YORK_TIMEZONE), 'day') ||
        DateTime.fromISO(date)
            .setZone(NEW_YORK_TIMEZONE)
            .hasSame(DateTime.now().setZone(NEW_YORK_TIMEZONE).minus({ days: 1 }), 'day')
    );
};

const isoToDateString = (iso: string | null | undefined): string | null => {
    if (!iso) return null;
    const dt = DateTime.fromISO(iso);
    if (!dt.isValid) return null;
    return toDateString(dt);
};

const sleep = async (ms: number) => {
    return new Promise((resolve) => setTimeout(resolve, ms));
};

/**
 * Formats a duration in seconds as "MM:SS" (or "H:MM:SS" when ≥ 1 hour).
 * Used for short live countdown displays, e.g. knockout market timers.
 */
export const countdownFormatter = (seconds: number): string => {
    const totalSeconds = Math.max(0, Math.floor(seconds));
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    const mm = m.toString().padStart(2, '0');
    const ss = s.toString().padStart(2, '0');
    return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
};

/**
 * Formats a closing-soon duration omitting seconds when ≥ 1 minute, so the
 * display doesn't change every second for long-duration countdowns.
 *   22 hours  → "22h 0m"
 *   45m 30s   → "45m 30s"
 *   30s       → "30s"
 */
export const closingSoonFormatter = (seconds: number): string => {
    const totalSeconds = Math.max(0, Math.floor(seconds));
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
};

export {
    currentUnixTime,
    daysSince,
    daysUntil,
    daysUntilRoundUp,
    durationFormatter,
    getIsClosed,
    getIsClosingSoon,
    getIsOpen,
    getLocalTimezoneAbbreviation,
    getOutsideTradingHoursMessage,
    getTradingHours,
    isoToDateString,
    isoToDateTimeStringWithTz,
    notificationTime,
    secondsLeftInDay,
    secondsLeftInMonth,
    secondsLeftInWeek,
    secondsLeftInYear,
    secondsTo,
    timeSince,
    timeTo,
    toDateString,
    toDateTimeStringWithTz,
    toShortDateTimeStringWithTz,
    toTimeStringWithTz,
    twitterTime,
    underMonth,
    formattedTimeToMidnightUTC,
    isTodayOrYesterday,
    sleep,
};
